// frontend/src/components/ui/LoadingSpinner.jsx (Alternative with more features)
import React from "react";
import Icon from "../AppIcon";

const LoadingSpinner = ({
  size = "md",
  variant = "primary", // primary, secondary, light, dark
  text = null,
  showText = true,
  fullScreen = false,
  overlay = false,
  className = "",
  spinnerClassName = "",
  textClassName = "",
}) => {
  const sizeClasses = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-8 w-8",
    lg: "h-12 w-12",
    xl: "h-16 w-16",
  };

  const textSizes = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
  };

  const variantClasses = {
    primary: "text-primary",
    secondary: "text-secondary",
    light: "text-white",
    dark: "text-foreground",
    success: "text-green-600",
    warning: "text-yellow-600",
    error: "text-red-600",
  };

  const defaultText = {
    xs: "Loading...",
    sm: "Loading...",
    md: "Loading...",
    lg: "Loading, please wait...",
    xl: "Loading, please wait...",
  };

  const displayText = text || defaultText[size];

  const spinner = (
    <div className={`${sizeClasses[size]} animate-spin ${spinnerClassName}`}>
      <Icon
        name="Loader"
        className={`${variantClasses[variant]}`}
        strokeWidth={
          size === "xs"
            ? 1
            : size === "sm"
            ? 1.5
            : size === "md"
            ? 2
            : size === "lg"
            ? 2.5
            : 3
        }
      />
    </div>
  );

  const content = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {spinner}
      {showText && displayText && (
        <p
          className={`mt-3 ${textSizes[size]} ${variantClasses[variant]} ${textClassName}`}
        >
          {displayText}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-background/90 backdrop-blur-sm flex items-center justify-center z-50">
        {content}
      </div>
    );
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-40 rounded-lg">
        {content}
      </div>
    );
  }

  return content;
};

// Skeleton loader for content loading states
export const SkeletonLoader = ({
  count = 1,
  height = "h-4",
  width = "w-full",
  className = "",
  rounded = "rounded",
}) => {
  const skeletons = Array.from({ length: count });

  return (
    <div className={`space-y-3 ${className}`}>
      {skeletons.map((_, index) => (
        <div
          key={index}
          className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${height} ${width} ${rounded}`}
        />
      ))}
    </div>
  );
};

// Card skeleton for trek cards
export const CardSkeleton = ({ count = 3 }) => {
  const skeletons = Array.from({ length: count });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletons.map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-sm border border-border overflow-hidden"
        >
          <div className="animate-pulse bg-gray-200 h-48 w-full" />
          <div className="p-6 space-y-4">
            <div className="animate-pulse bg-gray-200 h-6 w-3/4 rounded" />
            <div className="space-y-2">
              <div className="animate-pulse bg-gray-200 h-4 w-full rounded" />
              <div className="animate-pulse bg-gray-200 h-4 w-2/3 rounded" />
            </div>
            <div className="flex justify-between pt-4">
              <div className="animate-pulse bg-gray-200 h-8 w-24 rounded" />
              <div className="animate-pulse bg-gray-200 h-8 w-20 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LoadingSpinner;
